import { Traject } from "@/entities/dossier-details";
import { StationsAutocomplete } from "@/features/infra";
import { Button, FormErrorMessage, Label } from "@/shared/components";
import { useFieldArray, useFormContext, UseFormReturn, useWatch } from "react-hook-form";

type TrajectFieldValues = { trajecten: Traject[] };
type TrajectFormType = UseFormReturn<TrajectFieldValues>;

type TrajectenEditorProps = {
    label?: string;
};

export function TrajectenEditor({ label = "Traject(en)" }: TrajectenEditorProps) {
    const form = useSafeTrajectenForm();
    const { fields, append, remove } = useFieldArray({ control: form.control, name: "trajecten" });

    const trajectenPath = "trajecten";

    const handleAdd = () => {
        append({ vanStation: "", naarStation: "" });
        void form.clearErrors(trajectenPath);
    };

    const handleDelete = (index: number) => {
        remove(index);
    };

    const { errors } = form.formState;
    const trajectenError = errors.trajecten?.message ?? errors.trajecten?.root?.message;

    return (
        <div className="grid gap-2">
            <Label>{label}</Label>
            <ul className="grid gap-2" data-testid="trajecten">
                {fields.map((field, index) => (
                    <TrajectEditorRow key={field.id} form={form} index={index} onDelete={() => handleDelete(index)} />
                ))}
            </ul>
            <Button
                type="button"
                size="s"
                iconSize="xs"
                icon="plus"
                variant="tertiary"
                className="w-fit justify-start px-0 font-normal hover:bg-transparent hover:font-medium active:bg-transparent"
                onClick={handleAdd}
            >
                Traject
            </Button>
            {trajectenError && <FormErrorMessage>{trajectenError}</FormErrorMessage>}
        </div>
    );
}

const useSafeTrajectenForm = (): TrajectFormType => {
    // getting the types right for useFieldArray is difficult, so we don't pass generics for form / path to TrajectenEditor but use this runtime guard.
    const form = useFormContext<TrajectFieldValues>();

    if (!Array.isArray(form.getValues("trajecten"))) {
        throw new Error("Form must contain a property 'trajecten' that is an array");
    }
    return form;
};

type TrajectEditorRowProps<T extends TrajectFormType> = {
    form: T;
    index: number;
    onDelete: () => void;
};

function TrajectEditorRow<T extends TrajectFormType>({ form, index, onDelete }: TrajectEditorRowProps<T>) {
    const prefix = `trajecten.${index}` as const;
    const traject = useWatch({ control: form.control, name: prefix });
    const dataTestId = `traject-${traject.vanStation}-${traject.naarStation}`.toLowerCase();

    return (
        <li data-testid={`edit-${dataTestId}`}>
            <div className="flex items-center gap-1 has-[[data-slot='form-message']]:items-start">
                <StationsAutocomplete
                    showSearchIcon={false}
                    label={null}
                    className="flex-1"
                    form={form}
                    name={`${prefix}.vanStation`}
                    data-testid="reikwijdte-traject-vanStation"
                />
                <StationsAutocomplete
                    showSearchIcon={false}
                    label={null}
                    className="flex-1"
                    form={form}
                    name={`${prefix}.naarStation`}
                    data-testid="reikwijdte-traject-naarStation"
                />
                <Button
                    type="button"
                    iconOnly
                    icon="trash"
                    variant="tertiary"
                    title="traject verwijderen"
                    onClick={onDelete}
                    data-testid={`remove-${dataTestId}`}
                />
            </div>
        </li>
    );
}
